// Sélecteur de plage de dates avec presets compta + Apply explicite.
//
// Conception :
// - L'utilisateur édite des dates "en attente" (state local) sans rien actualiser.
// - Le bouton "Appliquer" propage `pendingStart` / `pendingEnd` vers `onApply()`.
// - Les presets appliquent immédiatement (à la sélection).

import { useState, useEffect } from "react";

interface Preset {
  key: string;
  label: string;
  range: () => { start: string; end: string };
}

function toIso(d: Date): string {
  // YYYY-MM-DD basé sur l'heure locale (pas UTC) — évite les off-by-one en fin/début de mois.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const PRESETS: Preset[] = [
  {
    key: "current-year",
    label: "Exercice en cours",
    range: () => {
      const now = new Date();
      return {
        start: `${now.getFullYear()}-01-01`,
        end: `${now.getFullYear()}-12-31`,
      };
    },
  },
  {
    key: "prev-year",
    label: "Exercice précédent",
    range: () => {
      const now = new Date();
      const y = now.getFullYear() - 1;
      return { start: `${y}-01-01`, end: `${y}-12-31` };
    },
  },
  {
    key: "ytd",
    label: "Depuis le 1er janvier",
    range: () => {
      const now = new Date();
      return { start: `${now.getFullYear()}-01-01`, end: toIso(now) };
    },
  },
  {
    key: "current-quarter",
    label: "Trimestre en cours",
    range: () => {
      const now = new Date();
      const q = Math.floor(now.getMonth() / 3);
      const start = new Date(now.getFullYear(), q * 3, 1);
      const end = new Date(now.getFullYear(), q * 3 + 3, 0);
      return { start: toIso(start), end: toIso(end) };
    },
  },
  {
    key: "current-month",
    label: "Mois en cours",
    range: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { start: toIso(start), end: toIso(end) };
    },
  },
  {
    key: "last-month",
    label: "Mois précédent",
    range: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: toIso(start), end: toIso(end) };
    },
  },
];

export function DateRangePicker({
  appliedStart,
  appliedEnd,
  onApply,
  isPending,
}: {
  appliedStart: string;
  appliedEnd: string;
  onApply: (start: string, end: string) => void;
  isPending?: boolean;
}) {
  const [pendingStart, setPendingStart] = useState(appliedStart);
  const [pendingEnd, setPendingEnd] = useState(appliedEnd);

  // Resync si l'externe change (ex: preset choisi)
  useEffect(() => {
    setPendingStart(appliedStart);
    setPendingEnd(appliedEnd);
  }, [appliedStart, appliedEnd]);

  const isDirty = pendingStart !== appliedStart || pendingEnd !== appliedEnd;
  const isValid = pendingStart !== "" && pendingEnd !== "" && pendingStart <= pendingEnd;
  const canApply = isDirty && isValid && !isPending;

  function selectPreset(presetKey: string) {
    const preset = PRESETS.find((p) => p.key === presetKey);
    if (!preset) return;
    const { start, end } = preset.range();
    onApply(start, end);
  }

  // Détecte le preset actuel s'il y en a un qui correspond aux dates appliquées
  const currentPresetKey = PRESETS.find((p) => {
    const { start, end } = p.range();
    return start === appliedStart && end === appliedEnd;
  })?.key ?? "";

  return (
    <div className="flex items-center gap-3">
      <select
        value={currentPresetKey}
        onChange={(e) => selectPreset(e.target.value)}
        className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
      >
        <option value="">Personnalisé</option>
        {PRESETS.map((p) => (
          <option key={p.key} value={p.key}>
            {p.label}
          </option>
        ))}
      </select>

      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-500">Du</span>
        <input
          type="date"
          value={pendingStart}
          onChange={(e) => setPendingStart(e.target.value)}
          className="border border-gray-300 rounded px-2 py-1"
        />
        <span className="text-gray-500">au</span>
        <input
          type="date"
          value={pendingEnd}
          onChange={(e) => setPendingEnd(e.target.value)}
          className={`border rounded px-2 py-1 ${isValid ? "border-gray-300" : "border-red-400"}`}
          title={isValid ? undefined : "La date de fin doit être postérieure ou égale à la date de début"}
        />
      </div>

      <button
        onClick={() => onApply(pendingStart, pendingEnd)}
        disabled={!canApply}
        className={`px-3 py-1 rounded text-sm font-medium ${
          canApply
            ? "bg-blue-600 hover:bg-blue-700 text-white"
            : "bg-gray-100 text-gray-400 cursor-not-allowed"
        }`}
      >
        {isPending ? "..." : "Appliquer"}
      </button>
    </div>
  );
}
